import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { client } from '@xinfer/api-client';
import type { Account, MintedKey } from '@xinfer/api-client';
import { KeyRevealModal } from '../components/KeyRevealModal';
import { formatTimestamp } from '../lib/format';

export function Keys({ account }: { account: Account }) {
  const queryClient = useQueryClient();
  const [minted, setMinted] = useState<MintedKey | null>(null);
  const [confirmingRevoke, setConfirmingRevoke] = useState<string | null>(null);

  const keys = useQuery({
    queryKey: ['keys', account.id],
    queryFn: () => client.listKeys(account.id),
  });

  const create = useMutation({
    mutationFn: () => client.createKey(account.id),
    onSuccess: (result) => {
      // Shown once, in a modal that cannot be dismissed by accident.
      setMinted(result);
      queryClient.invalidateQueries({ queryKey: ['keys', account.id] });
    },
  });

  const revoke = useMutation({
    mutationFn: (keyId: string) => client.revokeKey(account.id, keyId),
    onSuccess: () => {
      setConfirmingRevoke(null);
      queryClient.invalidateQueries({ queryKey: ['keys', account.id] });
    },
  });

  return (
    <>
      <div className="spread">
        <div>
          <h1>API keys</h1>
          <p className="subtitle">Used as the bearer token against the inference endpoint.</p>
        </div>
        <button className="primary" onClick={() => create.mutate()} disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create key'}
        </button>
      </div>

      {create.isError && <p className="error">Could not create a key.</p>}
      {keys.isLoading && <p className="muted">Loading keys…</p>}
      {keys.isError && <p className="error">Could not load keys.</p>}

      {keys.data && keys.data.data.length === 0 && (
        <p className="muted">No keys yet. Create one to start making requests.</p>
      )}

      {keys.data && keys.data.data.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Key</th>
              <th>Created</th>
              <th>Last used</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {keys.data.data.map((key) => (
              <tr key={key.key_id}>
                {/* Identified by its public half and last four characters.
                    The hash the gate authenticates against is never sent here. */}
                <td className="mono">
                  {key.key_id}
                  <span className="muted"> …{key.hint}</span>
                </td>
                <td className="muted">{formatTimestamp(key.created_at)}</td>
                <td className="muted">{formatTimestamp(key.last_used_at)}</td>
                <td>
                  {key.revoked ? (
                    <span className="badge dead">revoked</span>
                  ) : (
                    <span className="badge ok">active</span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {!key.revoked &&
                    (confirmingRevoke === key.key_id ? (
                      <span className="row">
                        <button
                          className="danger"
                          onClick={() => revoke.mutate(key.key_id)}
                          disabled={revoke.isPending}
                        >
                          Really revoke
                        </button>
                        <button className="ghost" onClick={() => setConfirmingRevoke(null)}>
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button className="ghost" onClick={() => setConfirmingRevoke(key.key_id)}>
                        Revoke
                      </button>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="muted" style={{ marginTop: 18 }}>
        Revoking is not instant everywhere: it reaches the serving path within a second or so
        under normal conditions, and may take longer if the control plane is unreachable.
      </p>

      {minted && <KeyRevealModal minted={minted} onClose={() => setMinted(null)} />}
    </>
  );
}
